import dynamic from 'next/dynamic';
import { ProductCategory } from 'lib/productCategory';
import { CollectionMenu } from 'lib/sellingProduct';
import { FooterMainProps } from './FooterMain';
import Container from '../Container';
import { useAuth } from 'lib/auth';
import { useMemo } from 'react';
import { routePaths } from 'appConfig';
import { useRouter } from 'next/router';

const FooterTop = dynamic(
  () => import('./FooterTop').then((mod) => mod.FooterTop),
  { ssr: false },
);

const FooterMain = dynamic<FooterMainProps>(
  () => import('./FooterMain').then((mod) => mod.FooterMain),
  { ssr: false },
);

interface FooterProps {
  collections?: CollectionMenu[];
  categories?: ProductCategory[];
  cateringEnabled?: boolean;
  ybExpressEnabled?: boolean;
}

const getCategoryNameBySlug = (slug: string): string => {
  switch (slug) {
    case 'customisable-meals':
      return 'Customisable Meals';
    case 'protein-bowls':
      return 'High Protein Meals';
    case 'snacks-and-chugs':
      return 'Healthy Snacks';
    case 'preppables':
      return 'DIY Meal Prep';
    case 'yummy-gear':
      return 'Yummy Gear';
    default:
      return '';
  }
};

export const Footer = ({
  collections = [],
  categories = [],
  cateringEnabled,
  ybExpressEnabled,
}: FooterProps) => {
  const { session } = useAuth();
  const { pathname } = useRouter();
  const isAuthenticated = !!session;

  const showDivider = useMemo(
    () =>
      ![routePaths.WEIGHT_LOSS_TIPS].includes(pathname) &&
      isAuthenticated,
    [isAuthenticated, pathname],
  );

  const sortedCollections = [...collections]
    .sort((a, b) => {
      const order = ['hot-sellers', 'new-launches'];

      return order.indexOf(a.slug) - order.indexOf(b.slug);
    })
    .map((collection) => {
      if (collection.slug === 'hot-sellers') {
        return {
          ...collection,
          name: 'Our Best Sellers',
        };
      }

      if (collection.slug === 'new-launches') {
        return {
          ...collection,
          name: 'New Launches',
        };
      }

      return collection;
    });

  const mappedCategories = categories.map((category) => ({
    ...category,
    name: getCategoryNameBySlug(category.slug),
  }));

  return (
    <footer className="tw-flex tw-flex-col tw-z-10 tw-w-full tw-bg-white">
      {showDivider && (
        <Container className="tw-py-0 tw-pb-4">
          <hr className="tw-my-0" />
        </Container>
      )}
      <FooterTop />
      <FooterMain
        collections={sortedCollections}
        categories={mappedCategories}
        cateringEnabled={cateringEnabled}
        ybExpressEnabled={ybExpressEnabled}
      />
    </footer>
  );
};
